import { TextField, MenuItem } from "@mui/material";
import type { ActionSetNodeProps } from "../actions/ActionSetNode";
import { PortHandle } from "../PortHandle";
import dico from "../../../../util/src/language/dico";
import { isTimerAction } from "../../../../util/src/behavior-timers/protocol";
import { BehaviorNodeCard } from "../BehaviorNodeCard.js";

export function TimerActionNode({
  node,
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  isPortConnected,
  onPortValueChange,
}: ActionSetNodeProps) {
  const labels = dico().behavior.timers;
  if (!isTimerAction(node.key)) return null;
  const flowInputs = node.ports.filter(
    (p) => p.direction === "input" && p.valueType === "flow",
  );
  const flowOutputs = node.ports.filter(
    (p) => p.direction === "output" && p.valueType === "flow",
  );
  const inputs = node.ports.filter(
    (p) => p.direction === "input" && p.valueType !== "flow",
  );
  const outputs = node.ports.filter(
    (p) => p.direction === "output" && p.valueType !== "flow",
  );
  const rows = [
    ...Array.from(
      { length: Math.max(flowInputs.length, flowOutputs.length) },
      (_, i) => ({ input: flowInputs[i], output: flowOutputs[i] }),
    ),
    ...Array.from(
      { length: Math.max(inputs.length, outputs.length) },
      (_, i) => ({ input: inputs[i], output: outputs[i] }),
    ),
  ];
  const renderPort = (p: (typeof node.ports)[number]) => {
    const connected = isPortConnected?.(node.id, p.id) ?? false;
    const choices =
      p.id === "unit"
        ? ["ms", "s", "min"]
        : p.id === "mode"
          ? ["countdown", "stopwatch"]
          : undefined;
    return (
      <div
        style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}
      >
        <PortHandle
          node={node}
          port={{ ...p, label: labels[p.id as keyof typeof labels] ?? p.label }}
          setPortsMap={setPortsMap}
          onMouseDown={() => onPortMouseDown?.(node.id, p)}
          onMouseUp={() => onPortMouseUp?.(node.id, p)}
          isConnected={connected}
          showValueInput={
            p.direction === "input" && p.valueType !== "flow" && !choices
          }
          onValueChange={(value) => onPortValueChange?.(node.id, p.id, value)}
        />
        {choices && !connected && (
          <TextField
            select
            size="small"
            value={p.value ?? choices[0]}
            aria-label={labels[p.id as keyof typeof labels]}
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => onPortValueChange?.(node.id, p.id, e.target.value)}
            sx={{
              minWidth: 130,
              "& .MuiInputBase-input": { fontSize: 12, py: 0.5 },
            }}
          >
            {choices.map((value) => (
              <MenuItem key={value} value={value}>
                {labels[value as keyof typeof labels]}
              </MenuItem>
            ))}
          </TextField>
        )}
      </div>
    );
  };
  return (
    <BehaviorNodeCard
      minWidth={310}
      title={labels.help}
      header={
        <>
          <span>{labels.title}</span>
          <span style={{ fontWeight: 600, opacity: 0.95 }}>
            {labels[node.key]}
          </span>
        </>
      }
      headerBackground="linear-gradient(180deg, #5eead4 0%, #0d9488 55%, #0f766e 100%)"
    >
      <div
        style={{ display: "flex", flexDirection: "column", gap: 4, padding: 8 }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {rows.map((row, index) => (
          <div
            key={index}
            style={{ display: "flex", alignItems: "center", gap: 10 }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              {row.input && renderPort(row.input)}
            </div>
            {(row.output || row.input?.valueType === "flow") && (
              <div style={{ flex: 1, minWidth: 0 }}>
                {row.output && renderPort(row.output)}
              </div>
            )}
          </div>
        ))}
      </div>
    </BehaviorNodeCard>
  );
}
